// src/components/legal/HealthCheckPrivacyPolicy.js
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Container = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 3rem 2rem;
    line-height: 1.8;
    color: #333;
`;

const Header = styled.header`
    text-align: center;
    margin-bottom: 3rem;
    padding-bottom: 2rem;
    border-bottom: 2px solid #eee;
`;

const Title = styled.h1`
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: #14B8A6;
`;

const UpdateDate = styled.p`
    color: #666;
    font-size: 1rem;
`;

const BackLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    color: #14B8A6;
    text-decoration: none;
    margin-bottom: 2rem;
    transition: opacity 0.3s;

    &:hover {
        opacity: 0.8;
    }

    &::before {
        content: '\2190';
        margin-right: 0.5rem;
    }
`;

const Section = styled.section`
    margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #444;
`;

const Paragraph = styled.p`
    margin-bottom: 1rem;
    text-align: justify;
    white-space: pre-wrap;
`;

const CompanyInfo = styled.div`
    background: #f8f9fa;
    padding: 1.5rem;
    border-radius: 10px;
    margin-bottom: 2rem;
    white-space: pre-wrap;
`;

const InfoBox = styled.div`
    background: #E0F7F5;
    padding: 1.5rem;
    border-radius: 10px;
    margin: 2rem 0;
    white-space: pre-wrap;
`;

const HealthCheckPrivacyPolicy = () => {
    return (
        <Container>
            <BackLink to="/healthcheck">헬스체크로 돌아가기</BackLink>

            <Header>
                <Title>헬스체크 개인정보처리방침</Title>
                <UpdateDate>최종 수정일: 2026년 10월 2일</UpdateDate>
            </Header>

            <CompanyInfo>
                <SectionTitle>데이터 컨트롤러 정보</SectionTitle>
                <Paragraph>
                    • 회사명: 마인드월드(MindWorld)
                    • 대표자: 윤우재
                    • 주소: 경기도 양주시 덕계로 137
                    • 사업자등록번호: 504-43-28680
                    • 이메일: mind-wellness@naver.com
                    • 서비스: 헬스체크(Health Check) 모바일 앱
                </Paragraph>
            </CompanyInfo>

            <Section>
                <SectionTitle>1. 개인정보 처리 개요</SectionTitle>
                <Paragraph>
                    마인드월드(MindWorld, 이하 "회사")는 사용자의 개인정보 보호를 매우 중요하게 생각합니다. 본 개인정보처리방침은 EU 일반데이터보호규정(GDPR), 캘리포니아 소비자 개인정보보호법(CCPA), 한국 개인정보보호법 등 관련 법규를 준수하여 작성되었습니다.

                    본 방침은 헬스체크 앱 및 관련 서비스 이용 시 수집, 처리, 저장되는 개인정보에 적용됩니다.

                    건강검진 결과 데이터는 민감정보(개인정보보호법 제23조)로서 특별히 보호되며, 건강 데이터는 기기 내 암호화 저장이 기본입니다. 앱 삭제·재설치 시 복원을 위해 검진 기록은 기기에서 암호화한 상태로만 서버에 백업되며, 복원 키는 사용자의 Google 계정에 보관되어 회사도 백업 내용을 볼 수 없습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>2. 개인정보 처리의 법적 근거</SectionTitle>
                <Paragraph>
                    회사는 다음의 법적 근거에 따라 개인정보를 처리합니다:

                    • 계약 이행: 서비스 제공을 위해 필요한 처리
                    • 동의: 사용자의 명시적 동의를 받은 처리
                    • 정당한 이익: 서비스 개선, 보안, 사기 방지 등
                    • 법적 의무 준수: 관련 법령에 따른 의무 이행

                    건강검진 결과 데이터는 사용자의 명시적 동의에 근거하여 처리되며, 언제든지 처리를 거부할 수 있습니다.

                    통계 활용(선택)은 별도의 선택 동의에 근거하며, 동의하지 않아도 서비스 이용에 제한이 없습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>3. 수집하는 개인정보의 항목</SectionTitle>
                <Paragraph>
                    필수 정보:
                    • 이메일 주소 (계정 생성 및 인증)
                    • 기기 정보 (OS 종류, 버전, 기기 식별자)
                    • 서비스 이용 로그

                    선택 정보:
                    • 성별
                    • 생년월일
                    • 키
                    • 몸무게

                    건강검진 관련 정보:
                    • 검진일
                    • 검진기관
                    • 검사 항목 수치
                    • 참고범위
                    • AI 분석 결과
                    • 결과지에 적힌 성명·생년월일 (본인 결과지 확인용, 기기에만 저장)
                    • 건강 데이터는 기기 내 암호화 저장(Hive)
                    • 검진 기록 백업본 (기기에서 암호화된 상태로만 서버에 저장, 회사는 열람 불가)
                    • 분석 이용 기록: 검진일, 검진기관, 검진 종류, 검사 항목 수, 주의·위험 항목 수 (검사 수치는 포함하지 않음)

                    AI 상담 정보:
                    • AI 상담 대화 내용 (상담 기록 확인 및 이용 횟수 관리)

                    결제 정보:
                    • 구독 상품, 구독 상태, 결제 시각 (결제 수단 정보는 Google Play가 처리하며 회사는 수집하지 않음)

                    통계 활용 정보 (선택 동의 시):
                    • 계정과 연결되지 않는 무작위 가명 ID, 성별, 연령대, 검진 연·월, 검진 종류, 검진기관, 검사 항목 수치·판정, 건강 점수
                    • 이름, 이메일, 생년월일, 계정 ID는 포함하지 않음

                    자동 수집 정보:
                    • 푸시 알림 토큰 (검진 알림 발송)
                    • 인식하지 못한 검사 항목명: 결과지에서 읽은 항목명 중 앱의 항목 사전에 없는 이름과, 해당 결과지의 검진 종류·검진기관명 (항목명 사전 개선용, 계정 ID·검사 수치는 포함하지 않으며 계정과 연결되지 않음)
                    • IP 주소 (지역 정보 확인용, 개인 식별 불가 형태로 처리)
                    • 앱 사용 패턴 및 성능 데이터
                    • 쿠키 및 유사 기술을 통한 정보
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>4. 개인정보 처리 목적</SectionTitle>
                <Paragraph>
                    서비스 제공:
                    • 계정 관리
                    • 검진 결과 OCR 인식 및 파싱
                    • AI 기반 건강 정보 분석
                    • 건강 상담 챗봇 제공
                    • 검진 비교 및 추이 분석
                    • 검진 기록 백업 및 복원 (앱 재설치·기기 변경 시)
                    • 본인 결과지 확인 (결과지 성명·생년월일 대조)
                    • 고객 지원 및 문의 응답

                    통계 활용 (선택 동의 시):
                    • 연령대·성별 검진 결과 통계 작성 및 건강 분석 기능 개선

                    서비스 개선:
                    • 새로운 분석 기능 개발
                    • 사용자 경험 분석 및 개선
                    • 서비스 안정성 및 보안 향상

                    법적 의무:
                    • 관련 법령에 따른 의무 이행
                    • 분쟁 해결 및 법적 절차 대응
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>5. 개인정보 보유 및 삭제</SectionTitle>
                <Paragraph>
                    일반 개인정보:
                    • 계정 활성 상태 동안 보유
                    • 계정 삭제 시 30일 이내 완전 삭제

                    건강검진 결과 데이터:
                    • 사용자가 직접 삭제하거나 계정 삭제 시 즉시 완전 삭제
                    • 암호화 백업본: 기기에 검진 기록이 없어지거나 건강 데이터 삭제·계정 삭제 시 즉시 삭제
                    • AI 분석 결과: 기기로 전달한 뒤 서버에서 삭제하며 보관하지 않음
                    • 백업 시스템에서도 30일 이내 완전 제거

                    AI 상담 대화 내용:
                    • 계정 활성 상태 동안 보유, 계정 삭제 시 즉시 삭제

                    통계 활용 데이터 (선택 동의 시):
                    • 계정과 연결되지 않은 형태로 저장되며, 통계 목적 달성 시까지 보관
                    • 계정과 연결되지 않아 계정 삭제 후에도 남으며, 특정 사용자의 데이터를 찾아 개별 삭제할 수 없음

                    인식하지 못한 검사 항목명:
                    • 계정과 연결되지 않은 형태로 저장되며, 항목명 사전 개선 목적 달성 시까지 보관
                    • 계정과 연결되지 않아 특정 사용자의 데이터를 찾아 개별 삭제할 수 없음

                    법적 보관 의무:
                    • 관련 법령에 따라 보관이 요구되는 경우 해당 기간까지 보관
                    • 결제 기록: 관련 법령에 따라 보관

                    삭제 방법:
                    • 전자 파일: 복구 불가능한 방식으로 완전 삭제
                    • 데이터베이스: 즉시 삭제 후 백업에서도 완전 제거
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>6. 개인정보 국제 전송</SectionTitle>
                <Paragraph>
                    서비스 제공을 위해 개인정보가 다음 지역으로 전송될 수 있습니다:

                    • 미국: Firebase (Google), Supabase 서버 위치
                    • 유럽연합: 일부 백업 및 보안 서비스

                    국외 이전 상세:
                    • 이전받는 자: Google LLC (Firebase, Gemini API, Google Play, Block Store), Supabase Inc. (데이터베이스)
                    • 이전 국가: 미국 등 각 사업자의 데이터센터 소재 국가
                    • 이전 항목: 3항의 수집 항목 중 각 서비스 제공에 필요한 항목 (AI 분석 시 검진 결과지 이미지·검사 수치, AI 상담 시 대화 내용)
                    • 이전 시기 및 방법: 서비스 이용 시점에 암호화된 네트워크(TLS)로 전송
                    • 보유 기간: 5항의 보유 기간 또는 위탁 계약 종료 시까지
                    • 거부 방법: 국외 이전을 거부할 수 있으나, 이 경우 AI 분석 등 서비스 이용이 제한됩니다

                    국제 전송 보호 조치:
                    • EU-US 데이터 프라이버시 프레임워크 준수
                    • 표준 계약 조항 (Standard Contractual Clauses) 적용
                    • 적절성 결정 국가로의 전송 우선
                    • 암호화 및 기타 기술적 보호조치 적용

                    EU 사용자의 경우 데이터 전송에 대한 이의를 제기할 권리가 있습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>7. 처리 위탁 및 제3자 제공</SectionTitle>
                <Paragraph>
                    회사는 사용자의 명시적 동의 없이 개인정보를 제3자에게 판매, 임대, 제공하지 않습니다. 통계 활용 데이터도 제3자에게 제공하지 않습니다.

                    서비스 제공을 위해 다음 업체에 개인정보 처리를 위탁합니다:

                    처리 위탁 업체:
                    • Google (Firebase): 인증, 분석, 알림, 오류 추적, 서버 기능 실행
                    • Google (Gemini API): 검진 결과지 이미지·PDF 인식, 검사 결과 설명 작성, AI 상담 응답 생성 (Gemini API 약관에 따라 처리되며, 회사는 결과지 이미지를 서버에 저장하지 않음)
                    • Google (Block Store): 검진 기록 백업 복원 키를 사용자의 Google 계정에 보관
                    • Supabase: 데이터베이스 호스팅 (암호화 백업본 포함)
                    • Google: Google Play Store 결제 처리

                    다음의 경우에만 제한적으로 공유됩니다:

                    법적 요구사항:
                    • 법원 명령, 소환장 등 법적 절차
                    • 사용자 또는 제3자 안전 보호를 위한 긴급 상황
                    • 사기 방지 및 보안 위협 대응

                    모든 서비스 제공업체는 엄격한 데이터 보호 계약에 따라 운영됩니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>8. 사용자 권리 및 선택</SectionTitle>
                <Paragraph>
                    모든 사용자 권리:
                    • 개인정보 열람권
                    • 정정 및 삭제권
                    • 처리 제한권
                    • 데이터 이동권 (내보내기)
                    • 이의 제기권

                    EU 사용자 추가 권리 (GDPR):
                    • 자동화된 의사결정 거부권
                    • 감독기관 신고권
                    • 효과적인 사법적 구제를 받을 권리

                    캘리포니아 사용자 권리 (CCPA):
                    • 개인정보 판매 거부권 (당사는 판매하지 않음)
                    • 차별받지 않을 권리

                    통계 활용 동의 철회:
                    • 앱 설정에서 언제든지 철회할 수 있으며, 철회 후에는 통계 데이터를 더 이상 전송하지 않습니다
                    • 이미 전송된 데이터는 계정과 연결되지 않아 개별 삭제할 수 없습니다

                    권리 행사 방법:
                    • 앱 내 설정 메뉴
                    • 이메일: mind-wellness@naver.com
                    • 요청 후 30일 이내 응답
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>9. 민감정보 보호</SectionTitle>
                <Paragraph>
                    건강검진 결과는 개인정보보호법 제23조에 따른 민감정보에 해당합니다.

                    민감정보 보호 원칙:
                    • 별도 동의 후 수집
                    • 기기 내 암호화 저장 원칙
                    • 명시적 동의 없이 제3자와 공유되지 않습니다
                    • 사용자는 언제든지 민감정보의 삭제를 요청할 수 있습니다

                    건강 데이터 처리:
                    • 건강검진 결과 수치는 기기 내 암호화 저장(Hive)
                    • 검진 결과지 이미지·PDF는 항목 인식을 위해 Google Gemini API 서버로 전송됩니다
                    • 회사는 결과지 이미지를 서버에 저장하지 않습니다
                    • 회사는 Gemini API 유료 등급을 이용하며, Google은 유료 등급 약관에 따라 전송된 데이터를 AI 모델 학습에 사용하지 않습니다 (오남용 방지를 위해 Google이 제한된 기간 보관할 수 있음)
                    • 결과지의 주민등록번호, 연락처, 주소는 인식 결과에서 제외하며, 성명·생년월일은 본인 결과지 확인을 위해 인식 후 기기에만 저장합니다
                    • 검사 결과 설명 작성을 위해 검사 항목·수치와 성별·나이가 Google Gemini API로 전송되며, 이름·이메일은 전송하지 않습니다
                    • 검사 항목의 판정(정상·주의·위험)과 건강 점수는 AI가 아닌 국가건강검진 판정기준(검진 당시 19세 미만은 학생 건강검사 판정기준), 학회 가이드라인, 결과지 참고범위에 따라 기기에서 계산합니다
                    • 사용자 동의 철회 시 모든 건강 데이터 즉시 삭제
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>10. 검진 기록 백업 및 복원</SectionTitle>
                <Paragraph>
                    앱 삭제·재설치 또는 기기 변경 시 검진 기록을 복원할 수 있도록 다음과 같이 백업합니다:

                    • 검진 기록은 기기에서 AES-256으로 암호화한 뒤 서버(Supabase)에 저장합니다
                    • 암호를 푸는 복원 키는 사용자의 Google 계정(Google Block Store)과 기기 보안 저장소에만 보관하며, 회사 서버에는 저장하지 않습니다
                    • 따라서 회사를 포함한 누구도 서버의 백업 내용을 볼 수 없습니다

                    복원 비밀번호 (선택):
                    • 다른 Google 계정을 쓰는 기기 등 복원 키를 찾을 수 없는 경우를 위해 복원 비밀번호를 설정할 수 있습니다
                    • 복원 키를 사용자가 정한 비밀번호로 한 번 더 암호화해 서버에 저장하며, 비밀번호 자체는 저장하지 않습니다
                    • 비밀번호를 잊으면 회사도 백업을 복원할 수 없습니다

                    백업 삭제:
                    • 기기의 검진 기록을 모두 삭제하거나 설정에서 건강 데이터를 삭제하면 서버의 백업본이 삭제됩니다
                    • 계정 삭제 시 백업본, 복원 비밀번호로 암호화한 키, 기기와 Google 계정에 보관된 복원 키가 모두 삭제됩니다
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>11. 통계 활용 (선택 동의)</SectionTitle>
                <Paragraph>
                    통계 활용에 동의한 경우에만 검진 결과를 통계 목적으로 별도 저장합니다:

                    • 목적: 연령대·성별 검진 결과 통계 작성 및 건강 분석 기능 개선
                    • 항목: 3항의 '통계 활용 정보'
                    • 처리 방식: 계정과 연결되지 않는 무작위 가명 ID로 저장하며, 이름·이메일·생년월일·계정 ID를 포함하지 않습니다. 같은 사용자의 연도별 변화를 보기 위해서만 가명 ID를 사용합니다
                    • 제3자 제공: 하지 않습니다
                    • 보유 기간: 통계 목적 달성 시까지
                    • 동의 거부 및 철회: 동의하지 않아도 모든 서비스를 이용할 수 있으며, 앱 설정에서 언제든지 철회할 수 있습니다. 철회 후에는 더 이상 전송하지 않으나, 이미 저장된 데이터는 계정과 연결되지 않아 개별 삭제할 수 없습니다
                    • 2026년 10월 이전 가입자는 동의하지 않은 상태로 설정되어 있으며, 설정에서 직접 동의한 경우에만 수집합니다
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>12. 쿠키 및 추적 기술</SectionTitle>
                <Paragraph>
                    회사는 다음 기술을 사용합니다:

                    필수 쿠키:
                    • 로그인 상태 유지
                    • 보안 및 사기 방지
                    • 기본 앱 기능 제공

                    분석 쿠키 (선택적):
                    • 앱 사용 통계 분석
                    • 성능 모니터링
                    • 사용자 경험 개선

                    사용자 선택권:
                    • 필수 쿠키: 서비스 이용을 위해 필요
                    • 분석 쿠키: 앱 설정에서 거부 가능
                    • 브라우저 설정에서 쿠키 관리 가능

                    EU 사용자는 쿠키 배너를 통해 동의를 관리할 수 있습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>13. 데이터 보안</SectionTitle>
                <Paragraph>
                    회사는 다음과 같은 보안 조치를 적용합니다:

                    기술적 보호조치:
                    • 종단간 암호화 (E2E)
                    • TLS/SSL 통신 암호화
                    • 정기적 보안 감사
                    • 침투 테스트
                    • 접근 권한 최소화 원칙

                    관리적 보호조치:
                    • 직원 보안 교육
                    • 정기적 보안 정책 검토
                    • 사고 대응 절차
                    • 제3자 보안 인증 요구

                    물리적 보호조치:
                    • 보안이 확보된 데이터센터 이용
                    • 24/7 모니터링
                    • 이중 인증 시스템

                    보안 사고 시 72시간 이내 관련 기관 및 사용자에게 통지합니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>14. 영업의 양도 등에 따른 개인정보 이전</SectionTitle>
                <Paragraph>
                    회사가 영업의 전부 또는 일부를 양도하거나 합병하는 경우, 개인정보보호법 제27조에 따라 개인정보가 이전될 수 있습니다.

                    • 이전 전에 이전 사실, 이전받는 자의 이름·연락처, 이전을 원하지 않을 경우의 조치 방법을 앱 내 공지 등으로 미리 알립니다
                    • 이전을 원하지 않는 사용자는 계정 삭제로 개인정보 이전을 거부할 수 있습니다
                    • 기기에만 저장된 건강 데이터와 회사가 열람할 수 없는 암호화 백업본의 내용은 이전 대상이 아닙니다
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>15. 연락처 및 데이터 보호 담당자</SectionTitle>
                <Paragraph>
                    개인정보 보호 관련 문의:

                    데이터 보호 담당자: 윤우재
                    이메일: mind-wellness@naver.com
                    회사: 마인드월드(MindWorld)
                    주소: 경기도 양주시 덕계로 137

                    EU 사용자 추가 연락처: 추후 서비스시 대리인 선임 예정.
                    • EU 대표자: [향후 지정 예정]
                    • 감독기관: 해당 국가 데이터 보호 기관

                    문의 응답 시간:
                    • 일반 문의: 7일 이내
                    • 권리 행사 요청: 30일 이내
                    • 긴급 보안 사안: 24시간 이내
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>16. 개인정보처리방침 변경</SectionTitle>
                <Paragraph>
                    본 방침의 변경 시 다음과 같이 공지합니다:

                    일반 변경사항:
                    • 시행 7일 전 앱 내 공지
                    • 이메일 또는 푸시 알림

                    중요 변경사항 (수집 항목, 목적, 제3자 제공 등):
                    • 시행 30일 전 사전 공지
                    • 명시적 동의 재요청 (필요시)

                    사용자 선택권:
                    • 변경사항에 동의하지 않을 경우 서비스 이용 중단 및 계정 삭제 가능
                    • 중요 변경사항의 경우 별도 동의 절차

                    과거 버전의 방침은 요청 시 제공 가능합니다.
                </Paragraph>
            </Section>

            <InfoBox>
                <SectionTitle>방침 정보</SectionTitle>
                <Paragraph>
                    • 최초 작성일: 2026년 3월 16일
                    • 최종 수정일: 2026년 10월 2일
                    • 시행일: 2026년 10월 2일
                    • 버전: 1.3
                    • 적용 법규: GDPR, CCPA, 한국 개인정보보호법
                </Paragraph>
            </InfoBox>
        </Container>
    );
};

export default HealthCheckPrivacyPolicy;
